import { uuidv4 } from '../../_Global/js/utils';
import { gsap } from "../../_Global/js/global";
import throttle from "lodash/throttle";

type TabFocusAction  = "previous" | "next" | "first" | "last";

function Tabs(component: HTMLElement): void {
  /**
   * The freqency (in ms) to run the window resize checks.
   */
  const resizeUpdateFrequency = 250;

  const tabButtonActiveClass = 'button--active';
  const tabContentActiveClass = 'tab--active';
  const tabButtons: NodeListOf<HTMLElement> = component.querySelectorAll('[role="tab"]');
  const tabPanels: NodeListOf<HTMLElement> = component.querySelectorAll('[role="tabpanel"]');
  const tabPanelsParent: HTMLElement = component.querySelector('[data-role="tab-panels-parent"]') as HTMLElement;
  const hasFixedTabHeight = component.getAttribute('data-fixed-tab-height') || '';
  const fixedTabHeightBreakpoint = parseInt( component.getAttribute('data-fixed-height-breakpoint') || '', 10 );
  let currentActiveTabIndex: number | null = null;
  let heightIsFixed = false;

  /**
   * Respond to key presses on a focussed tab
   */
  function onTabButtonKeyDown(evt: Event) {
    const focussedButton = evt.currentTarget as Element;
    const keyEvent = evt as KeyboardEvent;

    // React to left/right/home/end key presses as mandated by ARIA Practices

    if (keyEvent.code === 'ArrowLeft') {
      setTabButtonFocus(focussedButton, 'previous');
    }

    if (keyEvent.code === 'ArrowRight') {
      setTabButtonFocus(focussedButton, 'next');
    }

    if (keyEvent.code === 'Home') {
      setTabButtonFocus(focussedButton, 'first');
    }

    if (keyEvent.code === 'End') {
      setTabButtonFocus(focussedButton, 'last');
    }
  }

  /**
   * Respond to clicks on a tab button
   */
  function onTabButtonClick(evt: Event) {
    const animationDuration = 0.3; // in seconds
    const tabButton = evt.currentTarget as Element;
    const tabButtonIndex = parseInt( tabButton.getAttribute('data-tab-index') || '', 10 );
    const tabContent: HTMLElement | null = component.querySelector(`#${tabButton.getAttribute('aria-controls')}`);
    let tabContentHeight = 0;
    let previousTabButton: HTMLElement | null = null;
    let previousTabContent: HTMLElement | null = null;
    let previousTabContentHeight = 0;

    evt.preventDefault();

    // Stop any current animations
    gsap.killTweensOf(previousTabContent);
    gsap.killTweensOf(tabPanelsParent);
    gsap.killTweensOf(tabContent);

    if (currentActiveTabIndex !== null) {
      // There is already a tab open
      previousTabButton = tabButtons[currentActiveTabIndex];
      previousTabContent = tabPanels[currentActiveTabIndex];

      // Get the height of the previous tab content
      if (previousTabContent) {
        previousTabContentHeight = getHiddenElementHeight(previousTabContent);
      }

      // Remove styles and attributes from previous tab button
      previousTabButton?.classList.remove(tabButtonActiveClass);
      previousTabButton?.setAttribute('aria-selected', 'false');

      // this was preventing inactive tab buttons from being focused
      // which was flagged as an accessibility issue
      //previousTabButton?.setAttribute('tabindex', '-1');

      // Animate out the previous tab
      gsap.to(previousTabContent, {
        opacity: 0,
        duration: animationDuration,
        overwrite: true,
        onComplete: () => {
          // Set a height on the tab container to prevent layout jumps
          tabPanelsParent.style.height = `${previousTabContentHeight}px`;

          // Hide the previous tab content
          previousTabContent?.classList.remove(tabContentActiveClass);

          // Get the height of the new tab content
          if (tabContent) {
            tabContentHeight = getHiddenElementHeight(tabContent);
          }

          // Animate the tab container to the new content height
          gsap.to(tabPanelsParent, {
            height: `${tabContentHeight}px`,
            duration: animationDuration,
            overwrite: true,
            onComplete: () => {

              // Animate in the new tab content
              tabContent?.classList.add(tabContentActiveClass);

              gsap.fromTo(tabContent,
                  { opacity: 0 },
                  {
                    opacity: 1,
                    duration: animationDuration,
                    ease: 'power2.out',
                    overwrite: true,
                    onComplete: () => {
                      // Reset the tab parent height
                      tabPanelsParent.style.height = '';
                    }
                  }
              );
            }
          });
        }
      });
    } else {
      // No previous tab selected, just animate in the new content

      // Get the height of the new tab content
      if (tabContent) {
        tabContentHeight = getHiddenElementHeight(tabContent);
      }

      // Animate the tab container to the new content height
      gsap.to(tabPanelsParent, {
        height: `${tabContentHeight}px`,
        duration: animationDuration,
        overwrite: true,
        onComplete: () => {

          // Animate in the new tab content
          tabContent?.classList.add(tabContentActiveClass);

          gsap.fromTo(tabContent,
            { opacity: 0 },
            {
              opacity: 1,
              duration: animationDuration,
              ease: 'power2.out',
              overwrite: true,
              onComplete: () => {
                // Reset the tab parent height
                tabPanelsParent.style.height = '';
              }
            }
          );
        }
      });
    }

    // Add styles and attributes to the new tab button
    tabButton.classList.add(tabButtonActiveClass);
    tabButton.setAttribute('aria-selected', 'true');

    // this was preventing inactive tab buttons from being focused
    // which was flagged as an accessibility issue
    //tabButton.setAttribute('tabindex', '0');

    // Set the current active tab index
    currentActiveTabIndex = tabButtonIndex;
  }

  /**
   * Respond to the viewport being resized
   */
  function onWindowResize() {
    const tabPanelHeights: number[] = [];

    if (
      window.innerWidth >= fixedTabHeightBreakpoint
    ) {
      // Capture the tab content heights
      tabPanels.forEach( tabPanel => {
        tabPanelHeights.push( getHiddenElementHeight(tabPanel, true) );
      });

      // Sort the highest number to the first index
      tabPanelHeights.sort((a, b) => b - a);

      // Update all tab contents to be the same height
      tabPanels.forEach( tabPanel => {
        tabPanel.style.height = `${tabPanelHeights[0]}px`;
      });

      heightIsFixed = true;
    }

    if (
      window.innerWidth < fixedTabHeightBreakpoint &&
      heightIsFixed
    ) {
      // Update all tab contents to have a dynamic height
      tabPanels.forEach( tabPanel => {
        tabPanel.style.height = '';
      });

      heightIsFixed = false;
    }
  }

  /**
   * Change the currently focussed tab button.
   */
  function setTabButtonFocus(currentButton: Element, action: TabFocusAction) {
    const totalTabButtons = tabButtons.length;
    const currentButtonIndex = parseInt( currentButton.getAttribute('data-tab-index') || '', 10 );

    if (!isNaN(currentButtonIndex)) {
      // Current index value is a number

      if (action === 'next') {
        if (currentButtonIndex + 1 === totalTabButtons) {
          // Loop back to the start
          tabButtons[0].focus();
        } else {
          // Focus the next button
          tabButtons[currentButtonIndex + 1].focus();
        }
      }

      if (action === 'previous') {
        if (currentButtonIndex - 1 < 0) {
          // Loop around to the end
          tabButtons[tabButtons.length - 1].focus();
        } else {
          // Focus the previous button
          tabButtons[currentButtonIndex - 1].focus();
        }
      }

      if (action === 'first') {
        // Focus the first button
        tabButtons[0].focus();
      }

      if (action === 'last') {
        // Focus the last button
        tabButtons[tabButtons.length - 1].focus();
      }
    }
  }

  /**
   * Calculate the height of an element that is
   * current hidden i.e. display: none.
   *
   * Temporarily clone the element in the page
   * and hide it so we can calculate it's height.
   */
  function getHiddenElementHeight(element: HTMLElement, ignoreSetHeight = false): number {
    const elementFixedHeight = element.style.height;
    let clone: HTMLElement | null = null;
    let elementHeight = 0;

    // If the element has a fixed height, simply use that value
    if (elementFixedHeight && !ignoreSetHeight) {
      elementHeight = parseInt(elementFixedHeight, 10);
    } else {
      clone = element.cloneNode(true) as HTMLElement;

      Object.assign(clone.style, {
        display: 'block',
        height: 'auto',
        maxHeight: 'none',
        opacity: '0',
        overflow: 'visible',
        visibility: 'hidden'
      });

      element.after(clone);
      elementHeight = Math.ceil( clone.getBoundingClientRect().height );
      clone.remove();
    }

    return elementHeight;
  }

  /**
   * Generate and assign IDs and aria attributes to the
   * tab buttons and their associated content.
   */
  function assignTabsAttributesAndListeners() {
    tabButtons.forEach((button, index) => {
      const tabId = uuidv4();
      const buttonId = `tab-${tabId}-button-${index}`;
      const contentId = `tab-${tabId}-section-${index}`;
      const tabContent = tabPanels[index];
      
      // the tabs need to be focusable
      button.removeAttribute("tabindex");

      // Set the button ID
      button.setAttribute('id', buttonId);

      // Set a reference to it's position
      button.setAttribute('data-tab-index', index.toString());

      // Link to the content
      button.setAttribute('aria-controls', contentId);

      button.addEventListener('click', onTabButtonClick);
      button.addEventListener('keydown', onTabButtonKeyDown);

      // Set the content ID
      tabContent?.setAttribute('id', contentId);

      // Link to the button
      tabContent?.setAttribute('aria-labelledby', buttonId);
    });

    // Add the resize listener as required
    if (
        hasFixedTabHeight === 'true' &&
        !Number.isNaN(fixedTabHeightBreakpoint)
      ) {
      window.addEventListener("resize", throttle(
        onWindowResize,
        resizeUpdateFrequency,
        {
            leading: false,
            trailing: true
        }
      ));

      // Call the resize function to set the initial height
      onWindowResize();
    }
  }

  /**
   * When the component is initialised set the active tab
   * as provided in the component configuration.
   *
   * If none is provided, then no tab is active on load.
   */
  function setInitialTab() {
    const initialTabIndexString = component.getAttribute('data-initial-tab-index') || '';
    const initialTabIndexNumber = parseInt(initialTabIndexString, 10);
    const initialButton = tabButtons[ initialTabIndexNumber ];
    const initialContent = tabPanels[ initialTabIndexNumber ];

    if (
      initialButton &&
      initialContent
    ) {
      // Open the initial tab and content
      initialButton.classList.add(tabButtonActiveClass);
      initialButton.setAttribute('aria-selected', 'true');
      // this was preventing inactive tab buttons from being focused
      // which was flagged as an accessibility issue
      //initialButton.setAttribute('tabindex', '0');
      initialContent.classList.add(tabContentActiveClass);
      currentActiveTabIndex = initialTabIndexNumber;
    } else {
      // No initial tab set, make sure only the first tab is reachable
      // this was preventing inactive tab buttons from being focused
      // which was flagged as an accessibility issue
      //tabButtons[0]?.setAttribute('tabindex', '0');
    }

  }

  assignTabsAttributesAndListeners();

  setInitialTab();
};

export default Tabs;
